import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getLiveStartRequest,
  liveEditorPath,
  startLiveSession
} from '@utils/api/realtime/live-transcript.ts';
import { TokenExtension } from '@utils/extensions/token.extension.ts';
import { RealtimeConfig } from '@utils/types/editors/realtime.ts';
import {
  liveStartErrorKey,
  readSessionSource,
  rememberSessionSource,
  streamUrlIsValid
} from '@utils/editors/live-session-source.ts';

export const useManagedLiveStart = () => {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const requestId = params.get('live_start');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const mounted = useRef(true);
  const pending = useRef(false);
  const generation = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!requestId) return;
    const activeGeneration = generation.current;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const recover = async () => {
      try {
        const status = await getLiveStartRequest(requestId);
        if (!active || activeGeneration !== generation.current) return;
        if (status.session_id) {
          const source = readSessionSource(requestId);
          if (source) rememberSessionSource(status.session_id, source);
          navigate(liveEditorPath(status.session_id), { replace: true });
        } else if (status.state === 'starting') {
          setStarting(true);
          timer = setTimeout(() => {
            void recover();
          }, 1000);
        } else {
          setStarting(false);
          setError(t(liveStartErrorKey(status.error)));
        }
      } catch {
        if (
          active &&
          activeGeneration === generation.current &&
          !pending.current
        ) {
          setStarting(false);
          setError(t('live-start-error'));
        }
      }
    };
    void recover();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [navigate, requestId, t]);

  const start = useCallback(
    async (config: RealtimeConfig) => {
      if (pending.current) return;
      const activeGeneration = generation.current;
      if (!config.language) {
        setError(t('live-select-language'));
        return;
      }
      if (!TokenExtension.get().string || !config.streamUrl) {
        setError(t('live-start-error'));
        return;
      }
      if (!streamUrlIsValid(config.streamUrl)) {
        setError(t('live-source-url-invalid'));
        return;
      }
      pending.current = true;
      setStarting(true);
      setError(null);
      let id = requestId || crypto.randomUUID();
      try {
        if (requestId) {
          try {
            const previous = await getLiveStartRequest(requestId);
            if (!mounted.current || activeGeneration !== generation.current)
              return;
            if (previous.session_id) {
              navigate(liveEditorPath(previous.session_id), { replace: true });
              return;
            }
            // A confirmed failed start cannot recover by replaying its UUID.
            // Only an explicit Connect creates a replacement request; an
            // uncertain response always retains the original identity.
            if (previous.state === 'failed') id = crypto.randomUUID();
          } catch {
            // Keep the request ID when its outcome cannot be confirmed.
          }
        }
        if (!mounted.current || activeGeneration !== generation.current) return;
        rememberSessionSource(id, config);
        setParams({ live_start: id }, { replace: true });
        const status = await startLiveSession(
          id,
          config.streamUrl,
          config.language,
          config.modelType
        );
        if (!mounted.current || activeGeneration !== generation.current) return;
        if (status.session_id) {
          rememberSessionSource(status.session_id, config);
          navigate(liveEditorPath(status.session_id), { replace: true });
        } else if (status.state !== 'starting') {
          setParams({}, { replace: true });
          setError(t(liveStartErrorKey(status.error)));
        }
      } catch (failure) {
        if (mounted.current && activeGeneration === generation.current)
          setError(
            t(
              liveStartErrorKey(
                failure instanceof Error ? failure.message : null
              )
            )
          );
      } finally {
        pending.current = false;
        if (mounted.current && activeGeneration === generation.current)
          setStarting(false);
      }
    },
    [navigate, requestId, setParams, t]
  );

  const clearError = useCallback(() => setError(null), []);
  const reset = useCallback(() => {
    if (pending.current) return false;
    generation.current += 1;
    setStarting(false);
    setError(null);
    return true;
  }, []);
  return {
    start,
    starting,
    error,
    clearError,
    reset,
    isPending: () => pending.current
  };
};
